/**
 * MIY Fresh — scroll-entrance motion.
 *
 * Kept separate from miy-storefront.js so each file has one job, and separate
 * from theme.js so the Nest vendor file stays diffable against a future
 * theme update.
 *
 * WHY THIS FILE IS SHAPED THE WAY IT IS
 * -------------------------------------
 * The previous version of this system hid content UP FRONT with CSS — every
 * non-first section of every template sat at `opacity: 0` the instant
 * `.miy-motion` landed on `<html>`, gated on a `main > .shopify-section` +
 * position selector, and this file only ever REVEALED it. That is the same
 * shape as style.css:4369, which sets `.lazy-image { opacity: 0 }` and
 * clears it only once JS adds `.lazyloaded` — and it produced the identical
 * failure: the position heuristic ("the first section is the LCP element,
 * exempt it") was WRONG on six of this theme's thirteen templates, because
 * several sections render as `<div>` and several as `<section>` (the `tag`
 * schema key), which desynchronises any selector that counts by tag rather
 * than by measured position. On templates/product.json it exempted
 * `breadcrumb`, not `main-product` — the actual LCP element — so the
 * storefront's own product and collection pages sat invisible from first
 * paint until this script ran, on a store whose live LCP was already
 * Moderate. A hand-maintained exemption list would have "fixed" that for
 * today's templates and rotted the moment a merchant reordered one.
 *
 * This file inverts the whole approach instead: NOTHING is hidden up front.
 * scan() measures every candidate with getBoundingClientRect() and hides
 * ONLY the ones actually below the fold, by adding `.is-reveal-pending` —
 * a class custom-overrides.css keys its ENTIRE hidden state on. An element
 * on or above the fold at scan time is left completely alone: no class, no
 * transition, no observer, and therefore no way for it to ever be at
 * `opacity: 0`. That removes the LCP-hiding failure class structurally
 * rather than patching its one known instance:
 *
 *   1. The hidden state still lives behind the `miy-motion` class too —
 *      added by snippets/miy-motion-boot.liquid and removed by its watchdog
 *      if this file never registers — but that is belt-and-braces now, not
 *      the primary guarantee. The primary guarantee is that CSS alone can
 *      never hide anything: every `opacity: 0` rule requires
 *      `.is-reveal-pending`, and only this file's scan() ever adds it, and
 *      only to an element it just measured as below the fold. Marking and
 *      observing an element is wrapped in a try/catch (see scan() below) so
 *      a throw between the two statements cannot leave the element marked
 *      hidden with nothing watching it — the class is stripped on failure
 *      instead, failing open to plain, always-visible content. Beyond that,
 *      `window.miyMotion` is only assigned once the FIRST scan() call below
 *      has returned successfully, so if scan() itself throws — the one
 *      failure the per-element try/catch cannot cover, e.g.
 *      querySelectorAll() itself failing — `window.miyMotion` stays
 *      undefined and the boot snippet's 2-second watchdog strips
 *      `.miy-motion` from `<html>`, which is what makes that watchdog a real
 *      fail-open path again rather than a check that always finds the
 *      engine "present" regardless of whether scan() ever actually ran.
 *   2. ONE shared observer. The vendor's btyAnimationImageLoad built a new
 *      observer on every pass, which is exactly why it never re-fired after
 *      injection.
 *   3. Mount measurement. observe() DOES queue an initial entry reflecting a
 *      target's current intersection state — but asynchronously, so content
 *      the page arrived already scrolled to (Back button, #deep-link,
 *      restored session) would sit hidden for a frame or two waiting on
 *      that callback if it were marked pending at all. Measuring at scan
 *      time and leaving on/above-fold content untouched avoids that
 *      entirely — there is nothing to un-hide, so there is nothing that can
 *      flash.
 *   4. Revealing is permanent and unobserves. Nothing can un-reveal, and
 *      nothing that was left alone at scan time is ever revisited either.
 *   5. scan() is exposed on `window.miyMotion.scan`, though nothing outside
 *      this file calls it today — grep confirms zero external call sites.
 *      What actually covers the cart, quickview, tab filters and search is
 *      the MutationObserver near the end of this file, which re-runs scan()
 *      automatically on any subtree it sees injected, anywhere in the
 *      document, regardless of where the injection came from. `scan` stays
 *      public in case a real, narrower call site is ever wanted; narrowing
 *      the MutationObserver's own scope later would require wiring those
 *      call sites first, not just relying on this already being public.
 *   6. A one-time settle pass, after `load`. scan()'s below-the-fold
 *      measurement is a single snapshot that can go stale before an
 *      already-pending element's own IntersectionObserver callback fires —
 *      see the settle-pass comment near the end of this file for the case
 *      it covers and how it differs from what scan() already covers.
 */
( function() {
	'use strict';

	/* ==========================================================================
	   SECTION ALLOW-LIST — turn motion on for a whole section without ever
	   editing that section's file
	   --------------------------------------------------------------------------
	   WHAT THIS LIST DOES. Most sections on this storefront are VENDOR files
	   (product-grid, product-list, icon-box, banner, product-tabs,
	   main-collection, main-blog and so on) — they came with the paid Nest
	   theme and must never be hand-edited, or a future Nest update stops
	   applying cleanly. Left alone, that would cap motion at the handful of
	   MIY-owned files forever, because the rest of this system only ever
	   animates an element that carries `data-miy-reveal` IN ITS MARKUP (see
	   the big comment a little further down). This list is the one sanctioned
	   exception, and it still does not touch a single vendor file.

	   Shopify already wraps every section in a `<div>` that looks like this:

	       <div id="shopify-section-123" class="shopify-section newletter-section">

	   The second class (`newletter-section` here) comes from that section's
	   OWN `{% schema %}` block (`"class": "newletter-section"`) — Shopify
	   writes it onto the wrapper automatically, no theme file has to. It is a
	   far better handle than the wrapper's `id`: `shopify-section-123` is a
	   number generated per template instance, so it is different on every
	   page and cannot be written down in a list like this one. The class is
	   the same on every page that renders that section.

	   Listing a class below tells this engine "treat this section's wrapper
	   exactly as if a MIY-owned file had hand-written `data-miy-reveal` on
	   it." Nothing else changes. It is not a new door into the hidden state —
	   it is the SAME door, opened by a different hand. See applyAllowlist()
	   below for exactly what that guarantees and does not.

	   HOW TO ADD A SECTION
	     1. Open the section's `.liquid` file — read it, do not edit it — and
	        find its `{% schema %}` block, near the bottom of the file.
	     2. Look for a `"class"` key, e.g. `"class": "icon-box-section"`. That
	        string, exactly as written, is what you list below. If the
	        section's schema has no `"class"` key, it cannot be targeted this
	        way — ask a developer rather than adding an id or a guess.
	     3. Add a line to SECTION_ALLOWLIST below:
	            { sectionClass: 'icon-box-section' }
	        Optionally add a `variant` to choose the entrance feel — `settle`,
	        `rule`, `start` or `end`. Leave it out for the plain default rise.
	        The four variants are documented in assets/custom-overrides.css,
	        in the "HOW TO ANIMATE SOMETHING" block just above section 10.

	   HOW TO REMOVE A SECTION
	     Delete its line from SECTION_ALLOWLIST below. That is the entire
	     change — nothing else in this file, or in custom-overrides.css,
	     references this list by name.

	   THE TWO HARD RULES. Breaking either one is a regression, not a style
	   choice, and is exactly the failure class this whole file exists to rule
	   out:
	     1. NEVER add a section that holds a template's MAIN content:
	        main-product, main-collection, main-cart, main-blog, main-article,
	        main-search, the hero, the category strip, or the header/footer
	        chrome. Those sections ARE the page for the customer. This list
	        may only ever add DECORATIVE or SECONDARY sections — a promo
	        band, a trust strip, a merchandising row — never the thing the
	        customer came to see.
	     2. NEVER add a section that normally sits above the fold (visible
	        without scrolling). The entrance can never play there — scan()
	        (below) leaves an on/above-fold element completely alone — so
	        listing one is a dead entry at best. If you are not sure where a
	        section sits, leave it OFF this list and ask, rather than guess.
	        See applyAllowlist()'s own comment for why a mistaken entry here
	        still cannot hide anything even if this rule is missed.
	   ========================================================================== */
	var SECTION_ALLOWLIST = [
		// Newsletter band — a single large decorative block (heading, form,
		// image), always below the homepage fold when the merchant enables
		// it. `settle`: a small rise plus a 2% scale, the variant this
		// file's own docs recommend for "single large objects: banners, the
		// contact map, a hero image".
		{ sectionClass: 'newletter-section', variant: 'settle' },

		// Icon box / trust strip. Renders a `.grid > .grid-item` list (see
		// sections/icon-box.liquid), so its children already stagger
		// automatically the moment the container itself carries
		// `data-miy-reveal` — custom-overrides.css, "10.1 Grid stagger".
		// No `variant`: a container that wraps a grid is exempted from its
		// OWN fade by a dedicated `:has()` rule right after 10.1, so the
		// container's own variant choice is moot here — leaving it off
		// keeps that exemption unambiguous instead of adding a transform
		// that rule exists specifically to cancel.
		{ sectionClass: 'icon-box-section' },

		// Product grid and product list — the homepage's merchandising
		// rows (Trending, Top Rated, New Arrivals and similar). Same grid
		// shape, same reasoning as icon-box directly above: no variant, so
		// the grid stagger and its `:has()` exemption apply cleanly.
		{ sectionClass: 'product-grid-section' },
		{ sectionClass: 'product-list-section' }

		// Left OFF deliberately, not by oversight:
		//   - banner-section, image-banner-section, product-tabs-section,
		//     product-tabs-width-banner-section, product-banner-section —
		//     these sit high on the homepage, close enough to the hero that
		//     whether they land below the fold varies by viewport and by
		//     which merchandising blocks are enabled above them. Rule 2
		//     above says: if unsure, leave it off.
		//   - every main-* section (main-collection, main-blog,
		//     main-product, main-cart, main-article, main-search, and any
		//     other template's main content) — banned outright by rule 1.
		//   - the hero, the category strip, the header, the footer —
		//     banned outright by rule 1.
	];

	// Never opted in, even if some future entry above matches an element
	// carrying one of these — the same absolute ban custom-overrides.css's
	// "HOW TO ANIMATE SOMETHING" rule 1 places on hand-authored placements.
	// A `.shopify-section` wrapper is never any of these in this theme's
	// markup today, which is exactly why this guard is written explicitly
	// rather than assumed: it must keep holding even if a future allow-list
	// entry's class ever ends up matching more than a plain section wrapper.
	var FORBIDDEN_TAGS = [ 'IMG' ];
	var FORBIDDEN_CLASSES = [ 'lazy-image', 'miy-card__image' ];

	function isForbiddenAllowlistTarget( el ) {
		if ( FORBIDDEN_TAGS.indexOf( el.tagName ) !== -1 ) {
			return true;
		}
		for ( var i = 0; i < FORBIDDEN_CLASSES.length; i++ ) {
			if ( el.classList.contains( FORBIDDEN_CLASSES[ i ] ) ) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Give each matching, not-yet-opted-in `.shopify-section` wrapper under
	 * `scope` the same `data-miy-reveal` attribute a MIY-owned file would
	 * have hand-written. This function's ONLY job is deciding WHICH elements
	 * get that attribute — it never marks anything `.is-reveal-pending`,
	 * never touches an observer, and never measures the fold itself. Once an
	 * element has the attribute, it is indistinguishable from hand-authored
	 * markup to every line of code that runs after this one: scan()'s own
	 * below-the-fold measurement, its `.is-reveal-pending` gate, and its
	 * fail-open try/catch all still apply, completely unmodified. That is
	 * what makes this safe to add without reopening the automatic-hiding
	 * design this file's header comment describes — there is still exactly
	 * one door into the hidden state, this function just gives a second,
	 * explicit hand a way to knock on it.
	 *
	 * First match in SECTION_ALLOWLIST wins per element; a section is not
	 * expected to match more than one entry, since each lists a distinct
	 * section-schema class.
	 *
	 * Wrapped per-element in try/catch for the same reason scan()'s own
	 * mark-and-observe step is: one element throwing (a hostile classList, a
	 * detached node) must not abort the rest of the pass, and must not leave
	 * that one element half-tagged.
	 */
	/**
	 * True for a section wrapper that is a DIRECT child of <main>.
	 *
	 * `main >` and nothing looser, for the same reason custom-overrides.css
	 * scopes its `overflow-x: clip` band rule that way: the header and the
	 * footer render their own `.shopify-section` wrappers OUTSIDE
	 * `content_for_layout`, and neither is content the shopper came for.
	 * Animating the header would animate the nav on every page load.
	 */
	function isMainSection( el ) {
		var parent = el.parentElement;
		return !! parent && 'MAIN' === parent.tagName;
	}

	function applyAllowlist( scope ) {
		var wrappers = scope.querySelectorAll( '.shopify-section' );

		for ( var i = 0; i < wrappers.length; i++ ) {
			var el = wrappers[ i ];

			if ( el.hasAttribute( 'data-miy-reveal' ) || isForbiddenAllowlistTarget( el ) ) {
				continue;
			}

			try {
				var matched = false;

				for ( var j = 0; j < SECTION_ALLOWLIST.length; j++ ) {
					var entry = SECTION_ALLOWLIST[ j ];
					if ( el.classList.contains( entry.sectionClass ) ) {
						el.setAttribute( 'data-miy-reveal', entry.variant || '' );
						matched = true;
						break;
					}
				}

				// EVERY OTHER SECTION UNDER <main> GETS THE DEFAULT RISE.
				// The list above is now about CHOOSING A VARIANT, not about
				// choosing what participates — see this file's header for the
				// decision and what replaced the old rule.
				if ( ! matched && isMainSection( el ) ) {
					el.setAttribute( 'data-miy-reveal', '' );
				}
			} catch ( err ) {
				// Fail open: an element this pass could not safely tag is left
				// exactly as it was found — no attribute, never hidden, never
				// animated. Same shape as scan()'s own fail-open guard below.
			}
		}
	}

	// OPT-IN ONLY. `[data-miy-reveal]` is the entire candidate set — there is
	// no `main > .shopify-section` half any more. That half used to mark
	// EVERY section of EVERY template a candidate for hiding, which is what
	// let this system hide content nobody ever asked it to hide: three
	// separate invisible-content defects across this branch's history trace
	// back to that automatic marking (see section 10's header comment in
	// assets/custom-overrides.css for all three, including one confirmed
	// live in Chrome — a section correctly reached `.is-revealed` and its
	// child still computed `opacity: 0`). Removing it means an element can
	// now be hidden ONLY if a MIY-owned file explicitly opted it in with the
	// attribute — there is no second, implicit path into the hidden state.
	//
	// No position exemption of any kind — not `:first-child`, not
	// `:first-of-type`, nothing. Whether the one remaining candidate type is
	// hidden is still decided by measuring it, every scan, never by where it
	// sits among its siblings.
	var SELECTOR = '[data-miy-reveal]';
	var PENDING = 'is-reveal-pending';
	var REVEALED = 'is-revealed';
	// Applied by the settle pass only (see near the end of this file) to
	// suppress the entrance transition when an element is revealed outside
	// the normal observer flow — its only CSS job is `transition: none`.
	var INSTANT = 'is-revealed-instant';

	// Marks a pending element that was measured ON or ABOVE the fold. It is
	// hidden like any other, but custom-overrides.css section 18 gives it
	// `opacity: .1` instead of `0`.
	//
	// WHY .1, honestly: this started out as an LCP argument — that a
	// zero-opacity element is dropped from Largest Contentful Paint while a
	// tenth still counts. MEASURED ON THIS STORE, THAT DID NOT HOLD: at 0 and
	// at .1 the LCP element was the same product image and the medians were
	// within noise of each other (1820ms vs 1948ms over seven runs each). The
	// claim is left here only as a record that it was tested, not as a reason.
	//
	// The reason that survives is the one this whole file exists for: at .1 an
	// element that somehow never gets revealed is FAINT, not INVISIBLE. Three
	// invisible-content defects in this branch's history are what that
	// difference is worth, and it costs nothing.
	var ABOVE = 'is-reveal-abovefold';

	function isRevealed( el ) {
		return el.classList.contains( REVEALED );
	}

	function isPending( el ) {
		return el.classList.contains( PENDING );
	}

	/** Swap the hidden state for the revealed one. Permanent; nothing un-reveals. */
	function reveal( el ) {
		el.classList.remove( PENDING );
		el.classList.add( REVEALED );
	}

	// No IntersectionObserver: never mark anything `.is-reveal-pending`, so
	// nothing is ever hidden — the CSS has no hidden state that does not key
	// on that class. Motion is the optional part; the content is not.
	if ( ! ( 'IntersectionObserver' in window ) ) {
		window.miyMotion = { scan: function() {} };
		return;
	}

	/**
	 * The single shared observer.
	 *
	 * `rootMargin` holds the trigger back until the element is properly on
	 * screen, so the whole entrance plays where it can be seen rather than the
	 * viewer catching only its tail.
	 */
	var observer = new IntersectionObserver( function( entries ) {
		for ( var i = 0; i < entries.length; i++ ) {
			var entry = entries[ i ];

			if ( ! entry.isIntersecting ) {
				continue;
			}

			reveal( entry.target );
			observer.unobserve( entry.target );
		}
	}, { threshold: 0.05, rootMargin: '0px 0px -8% 0px' } );

	/**
	 * Measure everything under `root` that has not already been decided, and
	 * hide + observe only what is genuinely below the fold.
	 *
	 * Already-revealed and already-pending elements are skipped outright, not
	 * re-measured — re-marking an already-pending element `.is-reveal-pending`
	 * would be harmless (the class add is idempotent) but re-observing it on
	 * every rescan is not: the IntersectionObserver contract does not require
	 * a duplicate observe() to be a no-op in every implementation this file
	 * has to assume, and there is nothing to gain from it — the existing
	 * observation already covers this element until it reveals.
	 *
	 * Marking `.is-reveal-pending` and calling observer.observe() on the same
	 * element must not be split by a throw — either both happen, or neither
	 * state survives on that element. See the try/catch below.
	 *
	 * Public as `window.miyMotion.scan`, though no external call site
	 * actually invokes it today — the cart, quickview, tab filters and
	 * search are all covered instead by the MutationObserver near the end of
	 * this file, which re-runs scan() on any subtree it sees injected,
	 * regardless of where the injection came from. Kept public in case a
	 * real, narrower call site is ever wanted.
	 */
	/**
	 * Reveal the on-screen elements scan() just hid, on the next frame, in
	 * document order with a small stagger.
	 *
	 * TWO FRAMES, not one. The first frame is where the browser commits the
	 * hidden state it was just given; adding the revealed class in that same
	 * frame lets the two styles collapse into one computed change and the
	 * transition never runs — the element simply appears, which is the bug
	 * that made three of four animations dead CSS earlier in this branch's
	 * history. The second frame is when there is something to transition
	 * FROM.
	 *
	 * The stagger is written as an inline `--miy-reveal-delay` rather than
	 * driven by `nth-child`, because these are sections, not siblings in a
	 * grid: they have different parents and no shared index for CSS to count.
	 * It is capped for the same reason 10.1's grid stagger is — an uncapped
	 * cascade down a long page is a shopper watching an empty screen fill in.
	 */
	function playAboveFold( list ) {
		// 90ms rather than 70, and capped one step shorter. The per-element
		// entrance is longer now (0.95s, 1.15s for a large block), so the
		// sections need more space between them to read as separate arrivals
		// instead of a blur — but the TOTAL has to stay short, hence the lower
		// cap. Five steps of 90ms is 450ms of lead-in, against 420ms before.
		var STEP_MS = 90;
		var MAX_STEPS = 5;

		requestAnimationFrame( function() {
			requestAnimationFrame( function() {
				for ( var i = 0; i < list.length; i++ ) {
					var el = list[ i ];
					var step = i < MAX_STEPS ? i : MAX_STEPS;

					// The stagger is COSMETIC and gets its own try, deliberately
					// separate from the reveal below. Bundled together, a
					// failing style write would skip straight to the catch and
					// leave the element hidden — a decorative delay would be
					// deciding whether a shopper sees content. It cannot be
					// allowed to. Found by the DOM-stub test, which models an
					// element without a `style` object.
					try {
						el.style.setProperty( '--miy-reveal-delay', ( step * STEP_MS ) + 'ms' );
					} catch ( err ) {
						// No stagger for this one; it still reveals below.
					}

					try {
						el.classList.remove( PENDING );
						el.classList.remove( ABOVE );
						el.classList.add( REVEALED );
					} catch ( err ) {
						el.classList.remove( PENDING );
						el.classList.remove( ABOVE );
					}
				}
			} );
		} );
	}

	/**
	 * Last resort: reveal anything still hidden that the shopper can see.
	 *
	 * The boot snippet's watchdog answers "did the engine ever start". This
	 * answers a different question — "did an element the engine DID mark ever
	 * get let go" — and nothing else covers it. An IntersectionObserver
	 * callback that never fires, a frame that never runs because the tab was
	 * backgrounded during load, a scan() that marked an element the instant
	 * before a layout shift moved it into view: each leaves real content at
	 * `opacity: .1` with nothing else scheduled to help.
	 *
	 * Deliberately NOT a blanket "reveal everything still pending". Elements
	 * genuinely below the fold are supposed to stay pending — that is the
	 * feature. This only touches what is currently within the viewport, which
	 * is the only place a hidden element is a visible defect.
	 *
	 * gulffruits.com has no equivalent: block its script and its content stays
	 * invisible. That is the specific way this is better rather than merely
	 * similar.
	 */
	function sweepVisibleStuck() {
		var stuck = document.querySelectorAll( '.' + PENDING );

		for ( var i = 0; i < stuck.length; i++ ) {
			var el = stuck[ i ];

			try {
				var box = el.getBoundingClientRect();
				if ( box.top >= window.innerHeight || box.bottom <= 0 ) {
					continue;
				}

				el.classList.remove( PENDING );
				el.classList.remove( ABOVE );
				el.classList.add( INSTANT );
				el.classList.add( REVEALED );
				observer.unobserve( el );
			} catch ( err ) {
				el.classList.remove( PENDING );
				el.classList.remove( ABOVE );
			}
		}
	}

	function scan( root ) {
		var scope = root || document;

		// Tag any allow-listed section wrapper BEFORE the SELECTOR query
		// below, so a freshly-tagged wrapper is picked up in this same
		// pass — same as a hand-authored data-miy-reveal always has been.
		applyAllowlist( scope );

		var nodes = scope.querySelectorAll( SELECTOR );
		var aboveFold = [];

		for ( var i = 0; i < nodes.length; i++ ) {
			var el = nodes[ i ];

			if ( isRevealed( el ) || isPending( el ) ) {
				continue;
			}

			if ( el.getBoundingClientRect().top < window.innerHeight ) {
				// ON OR ABOVE THE FOLD: hide it and play it straight away.
				// This is the entrance a shopper sees on refresh, and it is
				// the whole reason the top of the page participates at all.
				//
				// It is NOT handed to the observer. An element already in
				// view may never produce an intersection change, so waiting
				// on one is how content gets stuck hidden — instead it is
				// queued for the next frame below, which is a guarantee
				// rather than a callback that may or may not come.
				//
				// ABOVE goes on with PENDING, atomically, so the softer
				// `opacity: .1` hidden state applies from the first paint.
				// Adding it afterwards would leave one frame at full zero,
				// which is one frame of LCP ineligibility.
				try {
					el.classList.add( PENDING );
					el.classList.add( ABOVE );
					aboveFold.push( el );
				} catch ( err ) {
					el.classList.remove( PENDING );
					el.classList.remove( ABOVE );
				}
				continue;
			}

			// Atomic, fail-open: if either statement throws, this element
			// must not end up marked hidden with nothing observing it. Strip
			// the class (harmless if it was never added) and move on to the
			// rest of `nodes` — one bad element must not abort the whole
			// scan, and it must never end up permanently invisible.
			try {
				el.classList.add( PENDING );
				observer.observe( el );
			} catch ( err ) {
				el.classList.remove( PENDING );
			}
		}

		if ( aboveFold.length ) {
			playAboveFold( aboveFold );
		}
	}

	// window.miyMotion is assigned only AFTER the scan() call below returns
	// successfully — not before. If scan() itself throws (something the
	// per-element try/catch inside it cannot cover, e.g. querySelectorAll()
	// failing outright), window.miyMotion must stay undefined so the
	// 2-second watchdog in miy-motion-boot.liquid sees it falsy and strips
	// `.miy-motion` from <html>, switching off every hidden state in one
	// move. Do not reorder this back above scan() — that would make the
	// watchdog check "did this script start running" instead of "did the
	// engine actually finish its first pass", which defeats the point of it.
	scan();

	window.miyMotion = { scan: scan };

	// Sections further down the document may not exist yet when a deferred
	// script runs against a streamed response. One more pass at DOMContentLoaded
	// costs nothing and is idempotent, because already-decided nodes are skipped.
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', function() { scan(); } );
	}

	/**
	 * Re-scan whatever gets injected.
	 *
	 * The cart, quickview, the tab filters and search all replace subtrees
	 * after load. A node injected into an already-scanned document is never
	 * measured, so it would never be hidden OR revealed — for genuinely
	 * below-the-fold injected content that means it stays fully visible where
	 * it should have entered on scroll (a missed animation, not a hidden
	 * product), but it is still worth catching for the same reason the mount
	 * scan exists: any content that plays no entrance is a component this
	 * file has failed to describe, not a broken one.
	 *
	 * Debounced with rAF because a filter change can replace forty cards in one
	 * burst and each would otherwise trigger its own full-document scan.
	 */
	if ( 'MutationObserver' in window ) {
		var pending = false;

		new MutationObserver( function( records ) {
			for ( var i = 0; i < records.length; i++ ) {
				if ( records[ i ].addedNodes.length ) {
					if ( ! pending ) {
						pending = true;
						requestAnimationFrame( function() {
							pending = false;
							scan();
						} );
					}
					return;
				}
			}
		} ).observe( document.documentElement, { childList: true, subtree: true } );
	}

	/**
	 * One settle pass, after `load`.
	 *
	 * THIS IS A DIFFERENT CASE FROM THE ONES scan() ALREADY COVERS. scan()
	 * answers "was this element below the fold at the moment it was first
	 * measured" — the mount case, and the Back-button/deep-link case in this
	 * file's header comment. Both of those are handled by measuring at scan
	 * time and never marking an on/above-fold element at all.
	 *
	 * What scan() cannot answer is "did an element it correctly measured as
	 * below the fold get promoted ABOVE the fold before its own
	 * IntersectionObserver callback ever fired". That gap is real: a hero
	 * image finishing decode and shrinking the layout above it, a webfont
	 * swap reflowing text, a filter re-render collapsing content, or a
	 * Back-navigation scroll restoration landing after the synchronous first
	 * pass can all move an already-pending element into view between scan()
	 * marking it and the observer catching up. scan() deliberately never
	 * re-measures an already-pending element (see its own comment above), so
	 * nothing else in this file re-checks it — it would otherwise sit at
	 * `opacity: 0` until the observer eventually fires, then play the full
	 * entrance transition on content the viewer can already see.
	 *
	 * `is-revealed-instant` was removed earlier in this branch as dead
	 * machinery, correctly, for the mount case: nothing is ever hidden at
	 * mount that then needs an instant, transition-free reveal, because
	 * on/above-fold elements are never marked in the first place. Do NOT
	 * delete this settle pass, or `is-revealed-instant`, on the same
	 * reasoning a second time — this is the restored, SCOPED replacement for
	 * the one case that reasoning does not cover. It only ever touches
	 * elements scan() already marked pending, and only the ones that have
	 * moved above the fold since; genuinely below-the-fold elements are left
	 * exactly as scan() left them, still mid-flight for their observer.
	 *
	 * `load` is the natural one-time hook: it fires after images and most
	 * font swaps have settled, later than DOMContentLoaded, and only once —
	 * `{ once: true }` is belt-and-braces, not load-bearing on its own.
	 */
	// The stuck-element sweep runs on a timer as well as at `load`, because
	// `load` is exactly the event that does not arrive when a third-party
	// asset hangs — and a hung page is precisely when content is most likely
	// to be sitting hidden.
	//
	// THE DELAY MUST STAY CLEAR OF THE LONGEST ENTRANCE, or the sweep starts
	// snapping elements to `.is-revealed-instant` mid-animation and the effect
	// this system exists for is cut short on slow pages. The longest is a
	// twelve-item grid: 12 staggered starts at 80ms plus a 0.95s transition,
	// so 1.91s. 3.4s leaves roughly a second and a half of clearance.
	//
	// Retimed together with the tokens in custom-overrides.css section 9 — if
	// those change again, this number has to be recomputed, not assumed.
	window.setTimeout( sweepVisibleStuck, 3400 );
	window.addEventListener( 'load', sweepVisibleStuck );

	window.addEventListener( 'load', function() {
		var pendingNodes = document.querySelectorAll( '.' + PENDING );

		for ( var i = 0; i < pendingNodes.length; i++ ) {
			var el = pendingNodes[ i ];

			if ( el.getBoundingClientRect().top >= window.innerHeight ) {
				// Still genuinely below the fold: leave it exactly as scan()
				// left it, mid-flight for its own observer.
				continue;
			}

			// Same atomic, fail-open shape as scan()'s mark+observe: if
			// anything throws mid-update, make sure the element is not left
			// stuck in the hidden state.
			try {
				el.classList.remove( PENDING );
				el.classList.add( INSTANT );
				el.classList.add( REVEALED );
				observer.unobserve( el );
			} catch ( err ) {
				el.classList.remove( PENDING );
			}
		}
	}, { once: true } );
}() );
